import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Star, ClipboardCheck } from 'lucide-react';
import { interviewsAPI, evaluationsAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { NIVEL_LABELS } from './interviewUtils';

const avg = (vals) => { const v = vals.filter((x) => x); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : '-'; };

const Choice = ({ value, current, onClick, label, tone, testid }) => (
  <button type="button" onClick={() => onClick(value)} data-testid={testid}
    className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${current === value ? tone : 'border-slate-200 hover:bg-slate-50'}`}>{label}</button>
);

const StepRow = ({ step, score, onScore, testid }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border p-2.5" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-800">{step.nome}</p>
      <p className="text-xs text-slate-500">{step.pontuacao_ideal ? `Ideal do cargo: ${step.pontuacao_ideal}` : 'Sem pontuação ideal definida'}</p>
    </div>
    <div className="flex shrink-0">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onScore(n)} className="p-0.5" data-testid={`${testid}-star-${n}`}>
          <Star className={`w-5 h-5 ${n <= score ? 'fill-amber-400 text-amber-400' : n <= (step.pontuacao_ideal || 0) ? 'text-cyan-400' : 'text-slate-300'}`} />
        </button>
      ))}
    </div>
  </div>
);

export const InterviewResultDialog = ({ interview, open, onOpenChange, onSaved }) => {
  const [ctx, setCtx] = useState(null);
  const [scores, setScores] = useState({});
  const [form, setForm] = useState({ comparecimento: '', decisao: '', observacoes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !interview) return;
    setCtx(null);
    setForm({ comparecimento: interview.comparecimento || '', decisao: interview.decisao || '', observacoes: interview.observacoes_resultado || '' });
    interviewsAPI.evaluationContext(interview.id).then((r) => {
      setCtx(r.data);
      const s = {};
      (r.data.evaluation?.niveis || []).forEach((n) => (n.steps || []).forEach((st) => { s[`${n.nivel}|${st.nome}`] = st.pontuacao || 0; }));
      setScores(s);
    }).catch(() => toast.error('Erro ao carregar critérios'));
  }, [open, interview]);

  const save = async () => {
    if (!form.comparecimento) { toast.error('Informe se o candidato compareceu'); return; }
    if (form.comparecimento === 'compareceu' && !form.decisao) { toast.error('Informe se foi aprovado ou reprovado'); return; }
    setSaving(true);
    try {
      let evaluationId = null;
      if (form.comparecimento === 'compareceu') {
        const niveis = ctx.niveis.map((n) => ({ nivel: n.nivel, nome: n.nome, comentario: '',
          steps: n.steps.map((st) => ({ nome: st.nome, pontuacao: scores[`${n.nivel}|${st.nome}`] || 0, observacao: '' })) }));
        const body = { niveis, resumo_geral: form.observacoes, recomendacao: form.decisao === 'aprovado' ? 'contratar' : 'nao_contratar', status: 'finalizado' };
        const r = ctx.evaluation
          ? await evaluationsAPI.update(ctx.evaluation.id, body)
          : await evaluationsAPI.create({ ...body, candidate_id: interview.candidate_id, job_id: interview.job_id, pontos_fortes: [], pontos_melhoria: [] });
        evaluationId = r.data.id;
      }
      await interviewsAPI.result(interview.id, { comparecimento: form.comparecimento, decisao: form.decisao || null,
        evaluation_id: evaluationId, observacoes_resultado: form.observacoes });
      toast.success('Resultado registrado');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar resultado'); } finally { setSaving(false); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="interview-result-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-cyan-600" />Resultado da entrevista</DialogTitle>
          <DialogDescription>{interview?.candidate_name} · {interview?.job_title}</DialogDescription>
        </DialogHeader>
        {!ctx ? <Loader2 className="w-6 h-6 animate-spin text-cyan-600 mx-auto my-8" /> : (
          <div className="space-y-5">
            <div className="space-y-1.5"><Label>Comparecimento</Label>
              <div className="flex gap-2">
                <Choice value="compareceu" current={form.comparecimento} onClick={set('comparecimento')} label="Compareceu" tone="border-emerald-500 bg-emerald-50 text-emerald-800 font-medium" testid="result-attended" />
                <Choice value="faltou" current={form.comparecimento} onClick={set('comparecimento')} label="Faltou" tone="border-red-400 bg-red-50 text-red-700 font-medium" testid="result-missed" />
              </div>
            </div>
            {form.comparecimento === 'compareceu' && (
              <>
                <div className="space-y-1.5"><Label>Decisão</Label>
                  <div className="flex gap-2">
                    <Choice value="aprovado" current={form.decisao} onClick={set('decisao')} label="Aprovado" tone="border-emerald-500 bg-emerald-50 text-emerald-800 font-medium" testid="result-approved" />
                    <Choice value="reprovado" current={form.decisao} onClick={set('decisao')} label="Reprovado" tone="border-red-400 bg-red-50 text-red-700 font-medium" testid="result-rejected" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Avaliação pelos níveis do cargo</Label>
                  {!ctx.tem_ideal && <p className="text-xs text-amber-700" data-testid="no-ideal-warning">Esta vaga não tem pontuação ideal definida na Engenharia de Cargo.</p>}
                  <Tabs defaultValue={ctx.niveis[0]?.nivel}>
                    <TabsList className="w-full grid grid-cols-3 sm:grid-cols-6 h-auto gap-1 p-1">
                      {ctx.niveis.map((n) => (
                        <TabsTrigger key={n.nivel} value={n.nivel} className="flex flex-col py-1.5 text-xs" data-testid={`result-tab-${n.nivel}`}>
                          <span>{NIVEL_LABELS[n.nivel] || n.nivel}</span>
                          <span className="font-bold text-amber-600">{avg(n.steps.map((st) => scores[`${n.nivel}|${st.nome}`]))}<span className="text-cyan-600 font-normal"> / {avg(n.steps.map((st) => st.pontuacao_ideal))}</span></span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {ctx.niveis.map((n) => (
                      <TabsContent key={n.nivel} value={n.nivel} className="space-y-2 mt-3">
                        <p className="text-xs text-slate-500">{n.nome} — {n.descricao}</p>
                        {n.steps.map((st, i) => (
                          <StepRow key={st.nome} step={st} score={scores[`${n.nivel}|${st.nome}`] || 0} testid={`result-step-${n.nivel}-${i}`}
                            onScore={(v) => setScores((s) => ({ ...s, [`${n.nivel}|${st.nome}`]: v }))} />
                        ))}
                      </TabsContent>
                    ))}
                  </Tabs>
                  <p className="text-xs text-slate-500">Estrelas amarelas: nota do candidato · azuis: pontuação ideal do cargo.</p>
                </div>
              </>
            )}
            <div className="space-y-1.5"><Label>Observações</Label>
              <Textarea rows={3} value={form.observacoes} onChange={(e) => set('observacoes')(e.target.value)} data-testid="result-notes-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Voltar</Button>
          <Button onClick={save} disabled={saving || !ctx} className="bg-cyan-600 hover:bg-cyan-700" data-testid="result-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salvar resultado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
